import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {TranslateModule} from '@ngx-translate/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {UserDTO} from "../authentication/models/login";
import {FlexLayoutModule} from '@angular/flex-layout';
import {MatSort} from '@angular/material/sort';
import {MatSelectModule} from '@angular/material/select';
import {MatOptionModule} from '@angular/material/core';
import {MatListModule} from '@angular/material/list';
import {MatAutocompleteModule, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {UsersService} from './services/users.service';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {BreadcrumbsComponent} from '../shared/components/breadcrumbs/breadcrumbs.component';
import {BreadcrumbsOptions} from "../shared/models/breadcrumbs-model";
import {AlertComponent} from '../shared/components/dialogs/alert/alert.component';
import {ConfirmDialogComponent} from '../shared/components/dialogs/confirm/confirm.component';
import {debounceTime} from 'rxjs';
import {MatTooltip} from '@angular/material/tooltip';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    TranslateModule,
    FlexLayoutModule,
    MatPaginator,
    MatSort,
    MatTableModule,
    MatSelectModule,
    MatOptionModule,
    MatListModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    BreadcrumbsComponent,
    MatTooltip,
  ],
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit {
  breadcrumbsData: BreadcrumbsOptions = {
    title: 'general.users',
    buttons: [
      {
        label: 'button.back',
        style: 'blue',
        router: '/orders-dashboard'
      }
    ]
  };

  @ViewChild(MatPaginator)
  paginator!: MatPaginator;

  @ViewChild(MatSort)
  sort!: MatSort;

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('form') userFormViewChild: any;

  userForm: FormGroup;
  userNotFound = false;
  userSelected = false;
  usersByUsername?: any[];
  filter = new FormControl('');
  isEdit = false;
  editingUserId: number | null = null;

  readonly dialog = inject(MatDialog);

  users: UserDTO[] = [];
  dataSource = new MatTableDataSource<UserDTO>();
  displayedColumns: string[] = ['username', 'name', 'email', 'profile', 'actions'];

  isLoading = false;

  pageable: PageEvent = {
    pageIndex: 0,
    pageSize: 5,
    length: 0
  };

  constructor(private formBuilder: FormBuilder, private usersService: UsersService) {
    this.userForm = this.formBuilder.group({
      username: ['', Validators.required],
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      profile: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.usernameAutocomplete();
    this.loadUsers();
    this.filter.valueChanges.subscribe((filterValue) => {
      this.pageable.pageIndex = 0;
      if (this.paginator) {
        this.paginator.firstPage();
      }
      this.loadUsers((filterValue ?? '').trim().toLowerCase());
    });
}

  loadUsers(filter: string = ''): void {
    this.usersService
        .GetUsers(this.pageable.pageIndex, this.pageable.pageSize, filter)
        .subscribe((response) => {
          this.dataSource.data = response.content;
          this.pageable.length = response.totalElements
        });
  }

  pageChange(event: PageEvent) {
    this.pageable.pageIndex = event.pageIndex;
    this.pageable.pageSize = event.pageSize;
    const filterValue = this.filter.value?.trim().toLowerCase() || '';
    this.loadUsers(filterValue);
  }

  usernameAutocomplete() {
    this.userForm.get('username')?.valueChanges.pipe(
      debounceTime(1000)
    ).subscribe((username) => {
      if (typeof username === 'string' && username.trim() !== '') {
        this.isLoading = true;
        this.userSelected = false;
        this.userNotFound = true;
        this.usersService.findUserByUsername(username).subscribe({
          next: (response) => {
            this.usersByUsername = response;
            this.isLoading = false;

            if (response.length === 0) {
              this.userForm.get('username')?.setErrors({notFound: true});
              this.userNotFound = true;
              this.autocompleteTrigger.closePanel();
            } else {
              this.userForm.get('username')?.setErrors(null);
              this.userNotFound = false;
            }
          },
          error: () => {
            this.isLoading = false;
            this.userForm.get('username')?.setErrors({notFound: true});
            this.autocompleteTrigger.closePanel();
          }
        });
      } else if (this.userForm.value.username === '') {
        this.userSelected = false;
        this.userForm.reset();
      } else if (username != null) {
        this.userSelected = true;
        this.userForm.patchValue({
          name: username.firstName && username.surname ? `${username.firstName} ${username.surname}` : username.name,
          email: username.email
        });
        this.userNotFound = false;
      }
    });
  }

  displayUsername(user: any) {
    return user ? user.username : '';
  }

  addUser() {
    if (this.userForm.valid) {
      const newUser: UserDTO = {
        id: 0,
        username: this.userForm.value.username.username,
        name: this.userForm.value.name,
        email: this.userForm.value.email,
        profile: this.userForm.value.profile,
      };

      this.usersService.PostUser(newUser).subscribe({
        next: () => {
          this.loadUsers();
          this.resetFormState();
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              title: 'user.titleSuccess',
              message: 'user.userAddedSuccess',
              param: {username: newUser.username}
            },
          });
        },
        error: (error) => {
          if (error.status === 409) {
            this.dialog.open(AlertComponent, {
              disableClose: true,
              data: {
                title: 'user.titleInformation',
                message: 'user.userConflict',
                param: {username: newUser.username}
              },
            });
          } else {
            this.dialog.open(AlertComponent, {
              disableClose: true,
              data: {
                title: 'user.titleInformation',
                message: 'user.userAddError'
              },
            });
          }
        },
      });
    }
  }

  updateUser() {
    if (this.userForm.valid && this.editingUserId !== null) {
      let form = this.userForm.getRawValue();
      const updatedUser: UserDTO = {
        id: this.editingUserId,
        username: form.username.username,
        name: form.name,
        email: form.email,
        profile: form.profile,
      };

      this.usersService.PutUser(updatedUser, this.editingUserId).subscribe(
        () => {
          this.loadUsers();
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              title: 'user.titleSuccess',
              message: 'user.userUpdatedSuccess',
              param: {username: updatedUser.username}
            },
          });
          this.cancelEdit();
        },
      );
    }
  }

  editUser(user: UserDTO) {
    this.isEdit = true;
    this.editingUserId = user.id;

    const username = {
      username: user.username,
      name: user.name,
      email: user.email
    };

    this.userForm.patchValue({
      username: username,
      email: user.email,
      name: user.name,
      profile: user.profile,
    }, {emitEvent: false});

    this.userForm.get('username')?.disable();
    this.userForm.get('email')?.disable();
    this.userForm.get('name')?.disable();

    this.userSelected = true;
    this.userNotFound = false;
  }

  deleteUser(user: UserDTO) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: {
        message: 'user.deleteUserConfirmation',
        param: { name: user.name }
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirm) {
        this.usersService.DeleteUser(user.id).subscribe(
          () => {
            this.loadUsers();
            this.dialog.open(AlertComponent, {
              disableClose: true,
              data: {
                title: 'user.titleSuccess',
                message: 'user.userDeletedSuccess',
                param: {username: user.username}
              },
            });
          },
        );
      }
      this.cancelEdit();
    });
  }

  cancelEdit() {
    this.isEdit = false;
    this.editingUserId = null;
    this.resetFormState();
  }

  resetFormState() {
    this.userForm.enable();
    this.userFormViewChild.resetForm();
  }
}
